// Shared labels and deterministic ranking. No email bodies or financial inputs leave the server.
const CAMPAIGN_STATUSES = ['draft', 'ready', 'paused', 'completed'];
const CAMPAIGN_LABELS = { draft: 'Draft', ready: 'Ready', paused: 'Paused', completed: 'Completed' };
const VENDOR_STAGES = ['identified', 'contacted', 'replied', 'qualified', 'in_discussion', 'closed_won', 'lost'];
const PRIORITIES = ['P0', 'P1', 'P2'];
const STAGE_LABELS = { identified: 'Identified', contacted: 'Contacted', replied: 'Replied', qualified: 'Qualified', in_discussion: 'In discussion', closed_won: 'Closed won', lost: 'Lost' };
const normalize = value => String(value || '').normalize('NFKC').trim().toLowerCase();
const unique = values => [...new Set(values.filter(Boolean))];
function phoneIdentity(value) {
    let digits = String(value || '').replace(/\D/g, '').replace(/^00/, '');
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    if (digits.length === 10) digits = `91${digits}`; // Same national default as the existing portal phone service.
    return digits.length >= 8 && digits.length <= 15 ? digits : '';
}
const words = value => unique((Array.isArray(value) ? value : String(value || '').split(/[,;\n]/)).map(normalize));
function vendorCapabilities(company) {
    const profiles = [company.vendorProfile, company.sourceCompany?.vendor_profile].filter(Boolean);
    return unique([...words(company.capabilityTags || []), ...words(company.sharedTags || []), ...words(company.tags || []),
        ...profiles.flatMap(profile => ['offerings', 'data_types', 'languages_offered', 'countries'].flatMap(key => words(profile[key] || [])))]);
}
function rankVendor(company, requirement = {}, relationship = {}, now = new Date()) {
    const capabilities = vendorCapabilities(company);
    const requested = unique(['modality', 'geography', 'demographics', 'collectionType', 'languages'].flatMap(key => words(requirement[key])));
    const matches = requested.filter(term => capabilities.some(tag => tag === term || tag.includes(term) || term.includes(tag)));
    const capabilityScore = requested.length ? Math.round(40 * matches.length / requested.length) : 0;
    const threads = Math.max(0, Number(relationship.threads) || 0), replies = Math.max(0, Number(relationship.replies) || 0);
    const last = relationship.lastAt ? new Date(relationship.lastAt) : null;
    const age = last && Number.isFinite(+last) ? Math.max(0, (+new Date(now) - +last) / 86400000) : Infinity;
    const recency = age <= 30 ? 20 : age <= 90 ? 15 : age <= 180 ? 10 : age <= 365 ? 5 : 0;
    const relationshipScore = Math.round(Math.min(15, threads * 3) + Math.min(25, replies * 5) + recency);
    const score = relationshipScore + capabilityScore;
    return { score, priority: score >= 70 ? 'P0' : score >= 40 ? 'P1' : 'P2', relationshipScore, capabilityScore,
        matches, requested, threads, replies, lastAt: last && Number.isFinite(+last) ? last.toISOString() : null,
        explanation: `${matches.length}/${requested.length} capability matches · ${threads} email threads · ${replies} vendor replies`,
        calculatedAt: new Date(now).toISOString(), method: 'relationship-60-capability-40-v1' };
}
function campaignStatus(plan) {
    if (plan.campaignStatus === 'completed' || (!plan.campaignStatus && plan.stage === 'Closed')) return 'completed';
    if (plan.campaignStatus === 'paused') return 'paused';
    return plan.name?.trim() && plan.emailTemplate?.subject?.trim() && plan.emailTemplate?.body?.trim()
        && plan.approvedBy && plan.approvedVersion === plan.contentVersion ? 'ready' : 'draft';
}
function renderCampaignEmail(template = {}, values = {}) {
    const allowed = ['vendorName', 'contactName', 'projectName'];
    const unknown = new Set();
    const render = text => String(text || '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_, key) => {
        if (!allowed.includes(key)) { unknown.add(key); return `{{${key}}}`; }
        return String(values[key] || '');
    });
    const subject = render(template.subject), body = render(template.body);
    return { subject, body, unknown: [...unknown] };
}
function campaignIssues(plan, vendors) {
    const issues = [];
    if (!plan.name?.trim()) issues.push('Give the campaign a name.');
    if (!plan.emailTemplate?.subject?.trim()) issues.push('Write the initial email subject.');
    if (!plan.emailTemplate?.body?.trim()) issues.push('Write the initial email message.');
    const { unknown } = renderCampaignEmail(plan.emailTemplate);
    if (unknown.length) issues.push(`Remove unsupported template fields: ${unknown.join(', ')}.`);
    if (!vendors.length) issues.push('Add at least one vendor to the shortlist.');
    if (vendors.some(vendor => !vendor.ownerId || vendor.ownerActive === false)) issues.push('Assign an active coordinator to every selected vendor.');
    if (vendors.some(vendor => !PRIORITIES.includes(vendor.priority))) issues.push('Set a priority for every vendor.');
    if (vendors.some(vendor => !vendor.recipient)) issues.push('Choose an active email recipient for every selected vendor.');
    if (vendors.some(vendor => vendor.unavailable)) issues.push('Remove unavailable or ineligible companies from the shortlist.');
    return issues;
}
function bandAvailability(plan, vendors, now = new Date()) {
    const released = new Set(['P0', ...(plan.releasedBands || [])]);
    for (let index = 1; index < PRIORITIES.length; index++) {
        const band = PRIORITIES[index], previous = PRIORITIES[index - 1];
        const previousRows = vendors.filter(vendor => vendor.priority === previous);
        const completed = released.has(previous) && previousRows.every(vendor => vendor.stage !== 'identified');
        const days = Number(plan.bandReleaseDays);
        const timed = released.has(previous) && days >= 1 && plan.startedAt && +new Date(now) >= +new Date(plan.startedAt) + days * index * 86400000;
        if (completed || timed) released.add(band);
    }
    return PRIORITIES.filter(band => released.has(band));
}
module.exports = { CAMPAIGN_STATUSES, CAMPAIGN_LABELS, VENDOR_STAGES, PRIORITIES, STAGE_LABELS, normalize, words, phoneIdentity, vendorCapabilities, rankVendor, campaignStatus, campaignIssues, renderCampaignEmail, bandAvailability };
